/* eslint-disable dot-notation */
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Claim } from '@cms-max/medicare-api-sdk'
import { Button } from 'components/Button/Button'
import { logAriaMessage } from 'redux/reducers/aria-log'
import { useDispatch } from 'react-redux'
import { ClaimDetailsField } from './ClaimDetailsField'
import { Alert } from '@cmsgov/ds-medicare-gov'

export interface ClaimProceduresProps {
  claim:Claim|undefined
}

export const ClaimProcedures: FC<ClaimProceduresProps> = ({ claim }) => {
  const { t } = useTranslation('claimsSearch', { keyPrefix: 'claimDetails' })
  const dispatch = useDispatch()
  const CLAIMS_PER_PAGE = 3
  const maxProcs = claim?.procedures.length || 0
  const [procLimit, setProcLimit] = useState(CLAIMS_PER_PAGE)
  const showResultsControllers = (maxProcs > CLAIMS_PER_PAGE) && (procLimit < maxProcs)
  const updateProcCount = (action: 'more' | 'all') => {
    switch (action) {
      case 'more':
        setProcLimit(procLimit + CLAIMS_PER_PAGE)
        dispatch(logAriaMessage(t('procedures.loadProceduresMessage')))
        break
      case 'all':
        setProcLimit(maxProcs)
        dispatch(logAriaMessage(t('procedures.loadAllProceduresMessage')))
        break
    }
  }

  const sectionClassNames = 'ds-u-margin-y--2 my-p-my--0'

  return (
    <>
      <h2 className='ds-text-heading--2xl ds-u-margin-y--0'>
        {t('procedures.claimDetailsProcsSubheading')}
      </h2>
      {claim?.procedures.slice(0, procLimit).map((proc, index) => (
        <div className='m-c-card ds-u-padding--2 ds-u-margin-top--2' key={index}>
          <div className='ds-l-row ds-u-border-bottom--1'>
            <div className='ds-l-lg-col ds-u-padding-x--3 ds-u-padding-bottom--2'>
              {proc.procedureCode && (
                <ClaimDetailsField
                  className={sectionClassNames}
                  label={t('procedures.procedureCode')}
                  value={proc.procedureCode}
                />
              )}
            </div>
          </div>
          <div className='ds-l-row ds-u-padding-x--1'>
            <div className='ds-l-lg-col'>
              {(proc.claimLineNumber !== undefined && proc.claimLineNumber !== null && proc.claimLineNumber !== '') && (
                <ClaimDetailsField
                  className={sectionClassNames}
                  label={t('procedures.claimLineNumber')}
                  value={proc.claimLineNumber}
                />
              )}
              {proc.startDate && (
                <ClaimDetailsField
                  className={sectionClassNames}
                  label={t('procedures.startDate')}
                  value={proc.startDate}
                />
              )}
              {proc.endDate && (
                <ClaimDetailsField
                  className={sectionClassNames}
                  label={t('procedures.endDate')}
                  value={proc.endDate}
                />
              )}
            </div>
            <div className='ds-l-lg-col'>
              {proc.revenueCode && (
                <ClaimDetailsField
                  className={sectionClassNames}
                  label={t('procedures.revenueCode')}
                  value={proc.revenueCode}
                />
              )}
              {proc.totalUnits && (
                <ClaimDetailsField
                  className={sectionClassNames}
                  label={t('procedures.totalUnits')}
                  value={proc.totalUnits}
                />
              )}
            </div>
            <div className='ds-l-lg-col'>
              {proc.amountCharged && (
                <ClaimDetailsField
                  className={sectionClassNames}
                  label={t('procedures.amountCharged')}
                  value={proc.amountCharged}
                />
              )}
              {proc.medicareApproved && (
                <ClaimDetailsField
                  className={sectionClassNames}
                  label={t('procedures.medicareApproved')}
                  value={proc.medicareApproved}
                />
              )}
              {proc.coInsurance && (
                <ClaimDetailsField
                  className={sectionClassNames}
                  label={t('procedures.coInsurance')}
                  value={proc.coInsurance}
                />
              )}
            </div>
            <div className='ds-l-lg-col'>
              {proc.youMayBeBilled && (
                <ClaimDetailsField
                  className={sectionClassNames}
                  label={t('procedures.youMayBeBilled')}
                  value={proc.youMayBeBilled}
                />
              )}
              {proc.appliedToDeductible && (
                <ClaimDetailsField
                  className={sectionClassNames}
                  label={t('procedures.appliedToDeductible')}
                  value={proc.appliedToDeductible}
                />
              )}
            </div>
          </div>
        </div>
      ))}
      {showResultsControllers && (
        <div className='ds-l-row ds-u-margin-top--4'>
          <div className='ds-l-col--12'>
            <Button
              className='ds-c-button ds-c-button--solid ds-c-button--alternate ds-u-margin-right--2 ds-u-margin-bottom--1'
              onClick={() => updateProcCount('more')}
              siteActivityProps={{
                core: {
                  component: 'Claims',
                  elementId: 'claims_viewmoreprocedures_click',
                  coreClickData: {
                    linkText: t('procedures.showMoreProcs')
                  }
                }
              }}
              data-testid='show-more-button'>
                {t('procedures.showMoreProcs')}
            </Button>
            <Button
              className='ds-c-button'
              onClick={() => updateProcCount('all')}
              siteActivityProps={{
                core: {
                  component: 'Claims',
                  elementId: 'claims_viewallprocedures_click',
                  coreClickData: {
                    linkText: t('procedures.showAllProcs')
                  }
                }
              }}
              data-testid='show-all-button'>
                {t('procedures.showAllProcs')}
            </Button>
          </div>
        </div>
      )}
      {/* @ts-ignore */}
      {claim?.medicareApprovedInfo &&
      <Alert className="ds-u-margin-top--4" hideIcon>
        {/* @ts-ignore */}
        <div className='my-c-claims-procedures--medicareApprovedInfo-wrapper' dangerouslySetInnerHTML={{ __html: claim?.medicareApprovedInfo }}/>
      </Alert>}
    </>
  )
}
